<template>
  <div>
    <b-modal
      id="promo-code-services"
      title="Services"
      size="xl"
      @hidden="emitSelectedServices"
    >
      <b-overlay
        variant="white"
        spinner-variant="primary"
        blur="0"
        opacity="1"
        rounded="sm"
        :show="fetchingZones"
      >
        <!-- Search and Stats -->
        <b-row class="mb-3">
          <b-col md="8">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search zones..."
              debounce="300"
            />
          </b-col>
          <b-col md="4" class="text-right">
            <b-badge variant="primary" class="p-2">
              {{ totalSelectedServices }} services selected
            </b-badge>
          </b-col>
        </b-row>

        <!-- Table -->
        <b-table
          :items="paginatedZones"
          :fields="tableFields"
          responsive
          hover
          :tbody-tr-class="rowClass"
        >
          <!-- Zone Name Column -->
          <template #cell(zone)="data">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <b-icon
                  :icon="
                    data.item._showDetails ? 'chevron-down' : 'chevron-right'
                  "
                  class="mr-2 cursor-pointer"
                  @click="toggleDetails(data.item)"
                />
                <strong>{{ data.item.city }}</strong>
              </div>
              <b-badge
                v-if="getSelectedServicesCount(data.item) > 0"
                variant="success"
                pill
              >
                {{ getSelectedServicesCount(data.item) }} /
                {{ data.item.services.length }}
              </b-badge>
            </div>
          </template>

          <!-- Actions Column -->
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="toggleDetails(data.item)"
            >
              {{ data.item._showDetails ? "Hide" : "Show" }} Services
            </b-button>
          </template>

          <!-- Row Details (Collapsible Content) -->
          <template #row-details="data">
            <b-card>
              <b-row class="mb-2">
                <b-col>
                  <b-button
                    size="sm"
                    variant="outline-success"
                    class="mr-2"
                    @click="selectAllServices(data.item)"
                  >
                    Select All
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="deselectAllServices(data.item)"
                  >
                    Deselect All
                  </b-button>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  v-for="(service, index) in data.item.services"
                  :key="index"
                  md="3"
                  class="mb-2"
                >
                  <b-form-checkbox
                    v-model="service.selected"
                    :class="{
                      'font-weight-bold text-success': service.selected,
                    }"
                  >
                    {{ service.serviceClass }}
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </b-card>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row v-if="filteredZones.length > perPage" class="mt-3">
          <b-col class="d-flex justify-content-center">
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredZones.length"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0"
            />
          </b-col>
        </b-row>

        <!-- No Results -->
        <b-row v-if="filteredZones.length === 0 && !fetchingZones" class="mt-3">
          <b-col class="text-center">
            <p class="text-muted">
              No zones found matching "{{ searchQuery }}"
            </p>
          </b-col>
        </b-row>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import {
  BModal,
  BRow,
  BCol,
  BFormCheckbox,
  BOverlay,
  BTable,
  BButton,
  BCard,
  BPagination,
  BBadge,
  BIcon,
  BFormInput,
} from "bootstrap-vue";
import { ref, computed } from "@vue/composition-api";

export default {
  components: {
    BModal,
    BRow,
    BOverlay,
    BCol,
    BFormCheckbox,
    BTable,
    BButton,
    BCard,
    BPagination,
    BBadge,
    BIcon,
    BFormInput,
  },
  props: {
    services: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, ctx) {
    const zones = ref([]);
    const fetchingZones = ref(false);
    const currentPage = ref(1);
    const perPage = ref(10);
    const searchQuery = ref("");
    const servicesCalls = [];

    // Table fields configuration
    const tableFields = [
      {
        key: "zone",
        label: "Zone Name",
        sortable: true,
      },
      {
        key: "actions",
        label: "",
        class: "text-right",
      },
    ];

    const fetchZones = async () => {
      try {
        fetchingZones.value = true;
        const res = await ctx.root.$store.dispatch("zones/fetchZones");
        zones.value = res.map((zone) => ({
          ...zone,
          _showDetails: false,
        }));
        const serviceCall = async (el) => {
          if (el.services) {
            el.services = el.services.map((service) => {
              const serviceClass = service.split("::")[1];
              const obj = {
                serviceClass:
                  serviceClass?.charAt(0)?.toUpperCase() +
                  serviceClass?.slice(1),
                selected: false,
              };
              if (props.services.length) {
                const item = props.services.find(
                  (ser) =>
                    ser.service_class === obj.serviceClass &&
                    ser.zone === el.city
                );
                if (item) obj.selected = true;
              }
              return obj;
            });
          }
        };
        zones.value.forEach((el) => {
          servicesCalls.push(serviceCall(el));
        });
        fetchingZones.value = false;
      } catch (error) {
        console.log(error);
      }
    };

    // Filtered zones based on search query
    const filteredZones = computed(() => {
      if (!searchQuery.value) return zones.value;
      const query = searchQuery.value.toLowerCase();
      return zones.value.filter((zone) =>
        zone.city.toLowerCase().includes(query)
      );
    });

    // Paginated zones
    const paginatedZones = computed(() => {
      const start = (currentPage.value - 1) * perPage.value;
      const end = start + perPage.value;
      return filteredZones.value.slice(start, end);
    });

    // Get count of selected services for a zone
    const getSelectedServicesCount = (zone) => {
      if (!zone.services) return 0;
      return zone.services.filter((s) => s.selected).length;
    };

    // Total selected services across all zones
    const totalSelectedServices = computed(() => {
      let count = 0;
      zones.value.forEach((zone) => {
        count += getSelectedServicesCount(zone);
      });
      return count;
    });

    // Toggle row details
    const toggleDetails = (item) => {
      item._showDetails = !item._showDetails;
    };

    // Select all services in a zone
    const selectAllServices = (zone) => {
      if (zone.services) {
        zone.services.forEach((service) => {
          service.selected = true;
        });
      }
    };

    // Deselect all services in a zone
    const deselectAllServices = (zone) => {
      if (zone.services) {
        zone.services.forEach((service) => {
          service.selected = false;
        });
      }
    };

    // Row class for highlighting zones with selected services
    const rowClass = (item, type) => {
      if (!item || type !== "row") return "";
      const selectedCount = getSelectedServicesCount(item);
      if (selectedCount > 0) {
        return "table-success";
      }
      return "";
    };

    const emitSelectedServices = () => {
      const arr = [];
      zones.value.forEach((zone) => {
        if (zone.services) {
          zone.services.forEach((service) => {
            if (service.selected) {
              arr.push({
                zone: zone.city,
                service_class: service.serviceClass,
              });
            }
          });
        }
      });
      ctx.emit("services-selected", arr);
    };

    fetchZones();

    return {
      zones,
      fetchingZones,
      emitSelectedServices,
      tableFields,
      currentPage,
      perPage,
      searchQuery,
      filteredZones,
      paginatedZones,
      getSelectedServicesCount,
      totalSelectedServices,
      toggleDetails,
      selectAllServices,
      deselectAllServices,
      rowClass,
    };
  },
};
</script>

<style scoped>
.promo-services__city {
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.cursor-pointer {
  cursor: pointer;
}

::v-deep .table-success {
  background-color: rgba(40, 199, 111, 0.1) !important;
}

::v-deep .table-success:hover {
  background-color: rgba(40, 199, 111, 0.2) !important;
}
</style>
